import { useCallback, useState } from 'react'

import {
  Badge,
  Button,
  CloseButton,
  Group,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { hasLength, useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IconPlus } from '@tabler/icons-react'
import { useTranslate } from 'react-admin'

import {
  type MonetizationPatternAttributesApiDTO,
  type MonetizationPatternCreateAttributesApiDTO,
} from '../../codegen/generated/Api.schemas'
import {
  usePostApiMonetizationPatterns,
  usePutApiMonetizationPatternsId,
} from '../../codegen/generated/monetization-patterns'
import { MonetizationPatternsHelper } from '../../helpers/MonetizationPatterns.helper'

type MonetizationPatternFormValues = {
  name: string
  hasPreroll: boolean
  hasPostroll: boolean
  fixedMidrollSeconds: number[]
  recurringMidrollStart: string
  recurringMidrollInterval: string
}

type MonetizationPatternFormProps = {
  pattern?: MonetizationPatternAttributesApiDTO
  onSuccess: () => void
  onClose: () => void
}

export const MonetizationPatternForm = ({
  pattern,
  onSuccess,
  onClose,
}: MonetizationPatternFormProps): JSX.Element => {
  const t = useTranslate()

  const [midrollInput, setMidrollInput] = useState('')
  const [midrollInputError, setMidrollInputError] = useState<string>()

  const createMutation = usePostApiMonetizationPatterns()
  const updateMutation = usePutApiMonetizationPatternsId()

  const validateRecurringMidroll = useCallback(
    (value: string, otherValue: string): string | undefined => {
      const seconds = MonetizationPatternsHelper.parseTimecode(value)

      if (value.trim().length > 0 && seconds === undefined) {
        return t('pages.monetizationPatterns.errors.invalidTimecode')
      }

      const otherSeconds = MonetizationPatternsHelper.parseTimecode(otherValue)

      return (seconds === undefined) === (otherSeconds === undefined)
        ? undefined
        : t('pages.monetizationPatterns.errors.recurringBothRequired')
    },
    [t],
  )

  const form = useForm({
    initialValues: {
      name: pattern?.name ?? '',
      hasPreroll: pattern?.hasPreroll ?? true,
      hasPostroll: pattern?.hasPostroll ?? true,
      fixedMidrollSeconds: pattern?.fixedMidrollSeconds ?? [],
      recurringMidrollStart: MonetizationPatternsHelper.formatOptionalTimecode(
        pattern?.recurringMidrollStartSeconds,
      ),
      recurringMidrollInterval: MonetizationPatternsHelper.formatOptionalTimecode(
        pattern?.recurringMidrollIntervalSeconds,
      ),
    },
    validate: {
      name: hasLength({ min: 1 }, t('pages.monetizationPatterns.errors.nameRequired')),
      recurringMidrollStart: (value: string, values: MonetizationPatternFormValues) =>
        validateRecurringMidroll(value, values.recurringMidrollInterval),
      recurringMidrollInterval: (value: string, values: MonetizationPatternFormValues) =>
        validateRecurringMidroll(value, values.recurringMidrollStart),
    },
  })

  const onAddMidroll = useCallback(() => {
    const seconds = MonetizationPatternsHelper.parseTimecode(midrollInput)

    if (seconds === undefined) {
      setMidrollInputError(t('pages.monetizationPatterns.errors.invalidTimecode'))
      return
    }

    setMidrollInputError(undefined)
    setMidrollInput('')
    form.setValues({
      fixedMidrollSeconds: MonetizationPatternsHelper.normaliseFixedMidrolls([
        ...form.values.fixedMidrollSeconds,
        seconds,
      ]),
    })
  }, [form, midrollInput, t])

  const onSubmit = useCallback(async () => {
    const data: MonetizationPatternCreateAttributesApiDTO = {
      name: form.values.name.trim(),
      hasPreroll: form.values.hasPreroll,
      hasPostroll: form.values.hasPostroll,
      fixedMidrollSeconds: MonetizationPatternsHelper.normaliseFixedMidrolls(
        form.values.fixedMidrollSeconds,
      ),
      recurringMidrollStartSeconds:
        MonetizationPatternsHelper.parseTimecode(form.values.recurringMidrollStart) ?? null,
      recurringMidrollIntervalSeconds:
        MonetizationPatternsHelper.parseTimecode(form.values.recurringMidrollInterval) ?? null,
    }

    try {
      if (pattern?.id) {
        await updateMutation.mutateAsync({ id: pattern.id, data })
      } else {
        await createMutation.mutateAsync({ data })
      }

      notifications.show({
        color: 'green',
        title: pattern?.id
          ? t('pages.monetizationPatterns.update.title')
          : t('pages.monetizationPatterns.create.title'),
        message: pattern?.id
          ? t('pages.monetizationPatterns.update.message', { name: data.name })
          : t('pages.monetizationPatterns.create.message', { name: data.name }),
      })
      onSuccess()
    } catch (error: any) {
      console.error(error)
      notifications.show({
        color: 'red',
        title: `${t('_generics.error')} - ${error.response?.status ?? ''}`,
        message:
          error.response?.data?.content?.message ??
          error.response?.data?.message ??
          t('_generics.unknownError'),
      })
    }
  }, [createMutation, form, onSuccess, pattern, t, updateMutation])

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="lg">
        <Stack gap="xs" mb="md">
          <Title size="h5">{t('pages.monetizationPatterns.generalInformation')}</Title>
          <TextInput
            {...form.getInputProps('name')}
            key={form.key('name')}
            label={t('_generics.name')}
            required
            w={320}
          />
        </Stack>

        <Stack gap="sm">
          <Title size="h5">{t('pages.monetizationPatterns.adBreaks')}</Title>

          <Group gap="lg" mb="md">
            <Switch
              {...form.getInputProps('hasPreroll', { type: 'checkbox' })}
              key={form.key('hasPreroll')}
              label={t('pages.monetizationPatterns.hasPreroll')}
            />
            <Switch
              {...form.getInputProps('hasPostroll', { type: 'checkbox' })}
              key={form.key('hasPostroll')}
              label={t('pages.monetizationPatterns.hasPostroll')}
            />
          </Group>

          <Stack gap={6}>
            <Text size="sm" fw={500}>
              {t('pages.monetizationPatterns.fixedMidrolls')}
            </Text>
            <Group gap="xs">
              {form.values.fixedMidrollSeconds.map(seconds => (
                <Badge
                  key={seconds}
                  size="lg"
                  variant="light"
                  rightSection={
                    <CloseButton
                      size={14}
                      onClick={() =>
                        form.setValues({
                          fixedMidrollSeconds: form.values.fixedMidrollSeconds.filter(
                            midroll => midroll !== seconds,
                          ),
                        })
                      }
                    />
                  }
                >
                  {MonetizationPatternsHelper.formatTimecode(seconds)}
                </Badge>
              ))}
            </Group>
            <Group gap="xs" mt="xs" align="flex-start">
              <TextInput
                size="xs"
                w={140}
                value={midrollInput}
                error={midrollInputError}
                placeholder={t('pages.monetizationPatterns.timecodePlaceholder')}
                onChange={event => {
                  setMidrollInput(event.currentTarget.value)
                  setMidrollInputError(undefined)
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    onAddMidroll()
                  }
                }}
              />
              <Button
                size="xs"
                variant="transparent"
                leftSection={<IconPlus size={12} />}
                onClick={onAddMidroll}
              >
                {t('pages.monetizationPatterns.addTimecode')}
              </Button>
            </Group>
          </Stack>

          <Group gap="sm" align="flex-start" mt="sm">
            <TextInput
              {...form.getInputProps('recurringMidrollStart')}
              key={form.key('recurringMidrollStart')}
              size="xs"
              w={140}
              label={t('pages.monetizationPatterns.recurringMidrollStart')}
              placeholder={t('pages.monetizationPatterns.timecodePlaceholder')}
            />
            <TextInput
              {...form.getInputProps('recurringMidrollInterval')}
              key={form.key('recurringMidrollInterval')}
              size="xs"
              w={140}
              label={t('pages.monetizationPatterns.recurringMidrollInterval')}
              placeholder={t('pages.monetizationPatterns.timecodePlaceholder')}
            />
          </Group>

          <Text size="xs" c="dimmed">
            {t('pages.monetizationPatterns.recurringNote')}
          </Text>
          <Text size="xs" c="dimmed">
            {t('pages.monetizationPatterns.minVideoLengthNote')}
          </Text>
        </Stack>

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            {t('_generics.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={!form.isValid()}
            loading={createMutation.isLoading || updateMutation.isLoading}
          >
            {t('_generics.save')}
          </Button>
        </Group>
      </Stack>
    </form>
  )
}
